import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Grid, Card, CardContent, Radio, FormControl, FormLabel, TextField, FormControlLabel, RadioGroup, SelectField, Option } from "@deere/fuel-react";
import SelfServiceButtons from "../SelfServiceButtons";
import { getSelfServiceSurveys } from "../../../../services/campaign.service";
import { getSurveyTemplates, getAvailableSurveyCodes, getSurveyLiterals, getTemplateBySurveyCode } from "../../../../services/survey.service";
import { useOktaAuth } from "@okta/okta-react";
import { generateSurveyCode, prepareSurveyData, removeUnwantedProperties } from "../../../../utils/utils";
import { ALL, CM, CRS_SURVEY_TEMPLATES, DEFAULT_COLUMNS, DEFAULT_LANGUAGE_CODE, DEFAULT_PART_SECTION_PAYLOAD, DEFAULT_PART_SECTION_TRANSLATIONS, FETCHING_DATA, IS_NEW_SURVEY_NO, IS_NEW_SURVEY_QUESTION, IS_NEW_SURVEY_YES, LBK_OK, LBL_ERROR, SELFSERVICE_DEFAULT_PAYLOAD, STATUS_DRAFT, SURVEY_NAME_LBL, SURVEY_NAME_PLACEHOLDER } from "../../../../utils/surveyLiterals";
import { ERR_DATA_FETCH, ERR_EXISTING_REVISION } from "../../../../utils/errorLiterals";
import { useUserInfo } from "../../../../providers/userProvider";
import { getSuppSurveyConfiguration } from "../../../../services/userService";
import { useLocation } from "react-router-dom";
import { GenericFuelModal } from "../../../../components/modals/GenericFuelModal";

const Step1Card = ({ fieldValidation, setFieldValidation, currentStep, activeStep, setActiveStep, payloadData, setPayloadData, setShowDraftSuccess }) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const location = useLocation();
  const { authState } = useOktaAuth();
  const [surveyCodes, setSurveyCodes] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [surveysByTemplate, setSurveysByTemplate] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [isNewSurvey, setIsNewSurvey] = useState(location?.state?.payloadData?.editSurvey !== undefined ? !location.state.payloadData.editSurvey : true);
  const [allSurveys, setAllSurveys] = useState([]);
  const [showRevisionErrorModal, setShowRevisionErrorModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setApiError(false);
      try {
        // If payloadData.id is available, we don't need to fetch survey codes
        if (!payloadData.id) {
          const surveyCd = await getAvailableSurveyCodes(authState.accessToken.accessToken);
          surveyCd && Array.isArray(surveyCd) ? setSurveyCodes(surveyCd) : setApiError(true);
        }
        const templates = await getSurveyTemplates(authState.accessToken.accessToken);
        templates && Array.isArray(templates) ? setTemplates(templates) : setApiError(true);
        const surveys = await getSelfServiceSurveys(authState.accessToken.accessToken);
        if (surveys && Array.isArray(surveys)) {
          setAllSurveys(surveys);
          setSurveysByTemplate(surveys.filter((survey) => survey.templateType === payloadData.templateType && userInfo.roles.includes(survey.adminRole)));
        } else setApiError(true);
      } catch (err) {
        console.log(err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [payloadData.id]);

  useEffect(() => {
    //If payloadData.id is available , that means this is an existing survey & we don't want to re generate the survey code
    if (payloadData && payloadData.description && surveyCodes && !payloadData.id) {
      if (isNewSurvey) {
        const code = generateSurveyCode(payloadData.description, surveyCodes);
        setPayloadData((prevState) => {
          const newSections = prevState.sections.map((sec) => {
            const secCode = `${code}_SEC${sec.showOrder}`;
            const newQuestions = sec.questions.map((que) => {
              return { ...que, code: `${secCode}_Q${que.showOrder}` };
            });
            return { ...sec, code: secCode, questions: newQuestions };
          });
          return { ...prevState, code: code, adminRole: `${code}_ADMIN`, sections: newSections };
        });
      }
    }
  }, [payloadData.description, surveyCodes, payloadData.id]);

  const isNewSurveyChangeHandler = (value) => {
    setIsNewSurvey(value);
    setPayloadData(SELFSERVICE_DEFAULT_PAYLOAD);
  }

  const templateChangeHandler = (e) => {
    setSurveysByTemplate(allSurveys.filter((survey) => survey.templateType === e.target.value && userInfo.roles.includes(survey.adminRole)));
    setPayloadData((prevState) => {
      let data;
      const matchingTemplate = templates?.find((template) => template.templateType === e.target.value);
      if (matchingTemplate) {
        data = matchingTemplate.templateContextPath;
      }
      return {
        ...prevState,
        templateType: e.target.value,
        sections: setDefaultSections(e.target.value, prevState.language),
        homeContextPath: data,
        defaultColumns: e.target.value === CRS_SURVEY_TEMPLATES.PART ? [...DEFAULT_COLUMNS] : [],
      };
    });
  };

  const setDefaultSections = (templateType, languages) => {
    const sections = [];
    if (templateType === CRS_SURVEY_TEMPLATES.PART) {
      DEFAULT_PART_SECTION_PAYLOAD.forEach((sec) => {
        const translations = sec.isDefault
          ? languages
              .filter((l) => l.languageCd !== DEFAULT_LANGUAGE_CODE)
              .map((l) => {
                const translatedText = DEFAULT_PART_SECTION_TRANSLATIONS[sec.description][l.languageCd];
                return { locale: l.languageCd, language: l.text, text: translatedText, longDescription: "" };
              })
          : [];
        sections.push({
          description: sec.description,
          active: true,
          code: payloadData.code + "_SEC" + sec.showOrder,
          showOrder: sec.showOrder,
          longDescription: "",
          questions: [],
          type: sec.type,
          translations: translations,
          links: [],
          isDefault: sec.isDefault,
        });
      });
    }
    return sections;
  };

  const fnValidate = () => {
    !payloadData || !payloadData.description
      ? setFieldValidation(true)
      : setActiveStep((prevState) => {
          return prevState + 1;
        });
  };

  const setupPayloadForNewSurvey = async (surveyId) => {
    try {
      if (surveyId) {
        const surveyData = await getSuppSurveyConfiguration(surveyId, ALL, authState.accessToken.accessToken);
        const literalData = await getSurveyLiterals(surveyId, null, authState.accessToken.accessToken);
        const notificationData = await getTemplateBySurveyCode(surveyData.code, authState.accessToken.accessToken);
        if (surveyData && literalData && notificationData) {
          //Checking if there is a existing revision in process
          const surveysByCode = allSurveys ? allSurveys.filter((s) => s.code === surveyData.code) : [];
          const existingDraftSurvey = surveysByCode.find((s) => s.templateStatus === STATUS_DRAFT && Number(s.templateRevision) > Number(surveyData.templateRevision));
          if (existingDraftSurvey) {
            setShowRevisionErrorModal(true);
          } else {
            const data = prepareSurveyData(surveyData, literalData, notificationData);
            if (data) {
              removeUnwantedProperties(data);
              data.editSurvey = true;
              data.active = false;
              data.templateStatus = STATUS_DRAFT;
              data.templateRevision = (Number(data.templateRevision) + 1).toString();
              setPayloadData(data);
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getSurveyNameForValue = () => {
    if (payloadData.description && payloadData.description!== "") {
      const index = surveysByTemplate.findIndex((survey) => survey.description === payloadData.description);
      return index > -1 ? surveysByTemplate[index].id : "";
    }
    return "";
  };

  return (
    <Card>
      <CardContent>
        {isLoading || apiError ? (
          <Grid container direction="column" justifyContent="space-around" minHeight={"60vh"}>
            <Grid item alignSelf="center">
              <Typography variant="h2">{isLoading ? FETCHING_DATA : ERR_DATA_FETCH}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container direction="column" justifyContent="space-between" minHeight={"60vh"} rowSpacing={4}>
            <Grid item>
              <Typography variant="h1">
                {currentStep.step} - {currentStep.title}
              </Typography>
            </Grid>
            <Grid item pl={5} pr={5}>
              <FormControl sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}>
                <FormLabel>{IS_NEW_SURVEY_QUESTION}</FormLabel>
                <RadioGroup required>
                  <FormControlLabel
                    control={<Radio />}
                    label={IS_NEW_SURVEY_YES}
                    checked={isNewSurvey}
                    name="isNewSurvey"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px!important" } }}
                    onChange={() => isNewSurveyChangeHandler(true)}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label={IS_NEW_SURVEY_NO}
                    checked={!isNewSurvey}
                    name="isNewSurvey"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px!important" } }}
                    onChange={() => isNewSurveyChangeHandler(false)}
                  />
                </RadioGroup>
              </FormControl>
              <br />
              <br />
              <SelectField label="Template Type" required sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }} onChange={(e) => templateChangeHandler(e)} value={payloadData.templateType}>
                {templates?.map((template) => {
                  if (template.templateType !== CM) //CM Template type to be enabled in future releases
                    return (
                      <Option key={template.templateType} value={template.templateType}>
                        {template.description}
                      </Option>
                    );
                })}
              </SelectField>
              <br />
              <br />
              {isNewSurvey ? (
                <TextField
                  label={SURVEY_NAME_LBL}
                  placeholder={SURVEY_NAME_PLACEHOLDER}
                  value={payloadData.description}
                  error={fieldValidation}
                  helperText={fieldValidation && t("bs_required")}
                  name="surveyName"
                  onChange={(event) => {
                    setFieldValidation(false);
                    setPayloadData((prevState) => {
                      return { ...prevState, description: event.target.value };
                    });
                  }}
                  sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
                  required
                />
              ) : (
                <SelectField
                  label={SURVEY_NAME_LBL}
                  placeholder={SURVEY_NAME_PLACEHOLDER}
                  value={getSurveyNameForValue()}
                  error={fieldValidation}
                  helperText={fieldValidation && t("bs_required")}
                  name="surveyName"
                  onChange={(event) => {
                    setFieldValidation(false);
                    setupPayloadForNewSurvey(event.target.value);
                  }}
                  sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
                  required
                >
                  <Option disabled hidden value="">
                    Select
                  </Option>
                  {surveysByTemplate.map((survey) => (
                    <Option key={survey.code} value={survey.id} >
                      {survey.description}
                    </Option>
                  ))}
                </SelectField>
              )}
            </Grid>
            <Grid item pr={5}>
              <Grid container justifyContent="flex-end">
                <SelfServiceButtons
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  fnValidate={fnValidate}
                  payloadData={payloadData}
                  setPayloadData={setPayloadData}
                  setShowDraftSuccess={setShowDraftSuccess}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
        <GenericFuelModal
          title={LBL_ERROR}
          message={ERR_EXISTING_REVISION}
          show={showRevisionErrorModal}
          handleClose={() => setShowRevisionErrorModal(false)}
          primaryButton={LBK_OK}
          handleSave={() => setShowRevisionErrorModal(false)}
        />
    </Card>
  );
};

export default Step1Card;
